import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';

/**
 * Customer-app common-slice hooks. Tiny TanStack-Query wrappers over
 * the public `/v1/version` and license-scoped `/v1/branding`
 * endpoints. The IframeShell consumes both — branding to colour the
 * chrome, version for the footer.
 */

export interface VersionInfo {
  service: string;
  env: string;
  commitSha: string;
  deployedAt: string | null;
  now: string;
}

export interface BrandingInfo {
  licenseId: string;
  logoUrl: string | null;
  themeJson: unknown;
}

/**
 * Public — does not require auth. The SPA fetches once on mount and
 * caches for the session; the staleTime aligns with the TanStack
 * default `staleTime: 30_000` set in `main.tsx`.
 */
export function useVersion(): { data?: VersionInfo; isLoading: boolean } {
  const { api } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<VersionInfo>('/v1/version'),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
  return { data, isLoading };
}

/**
 * Branding for the signed-in license. Skips the fetch when no session
 * is in context — sign-in screen has no licensed identity yet, so it
 * uses default theme.
 */
export function useBranding(): { data?: BrandingInfo; isLoading: boolean } {
  const { session, api } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['branding', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<BrandingInfo>('/v1/branding'),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });
  return { data, isLoading };
}

/**
 * Theme overrides shape carried inside `BrandingInfo.themeJson`.
 * Subset of CSS-variable identities used across `index.css`; only
 * the per-tenant-overridable ones are surfaced here. Anything else
 * keeps the default theme.
 */
export interface BrandingTheme {
  primary?: string;
  sidebar?: string;
  accent?: string;
  gradientStart?: string;
  gradientEnd?: string;
}

export interface BrandingUpdateInput {
  logoUrl?: string | null;
  themeJson?: BrandingTheme;
}

export function useUpdateBranding(): {
  update: (input: BrandingUpdateInput) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { session, api } = useAuth();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (input: BrandingUpdateInput) => api.put<unknown>('/v1/branding', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['branding', session?.licenseId ?? 'anon'] });
      toast.success('Branding saved');
    },
    onError: (err: Error) => toast.error(`Could not save branding: ${err.message}`),
  });
  return {
    update: (input) => m.mutateAsync(input),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}
